'use client';

import { CustomError, isEnumValue } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { scroller } from 'react-scroll';

import { COMMENT_SORT_TYPE, COMMENT_TYPE } from '@/api';
import { ROUTES, SEARCH_PARAMS, SELECTOR } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useToastPoint } from '@/hooks/useToastPoint';
import { useAuthStore } from '@/stores/AuthStore';
import {
  CONTENT_CATEGORY,
  GTM_EVENT_TYPE,
  trackGtmEvent,
} from '@/utils/analytics/gtm';

import { CommentField } from '../molecule/CommentField';
import type { CommentListProps } from '../organism/CommentList';
import { CommentList } from '../organism/CommentList';

export interface CommentListWithFieldProps
  extends Pick<
    CommentListProps,
    'commentType' | 'contentId' | 'initialData' | 'rootCommentId' | 'className'
  > {
  /** 글 제목 */
  title?: string;
  /** 정책 이름 */
  policyName?: string;
  initOnFocus?: boolean;
  hasCommentField?: boolean;
  commentFieldTopDivider?: boolean;
  commentFieldClassName?: string;
  withPortal?: boolean;
}

export const CommentListWithField = ({
  initOnFocus,
  commentType,
  contentId,
  title,
  policyName,
  initialData,
  rootCommentId,
  className,
  hasCommentField = true,
  commentFieldTopDivider,
  commentFieldClassName,
  withPortal,
}: CommentListWithFieldProps) => {
  const searchParams = useSearchParams();

  const sortType =
    searchParams?.get(SEARCH_PARAMS.SORT_TYPE) || COMMENT_SORT_TYPE.LATEST;

  const parentCommentId = ((params) => {
    if (!params) return undefined;

    const parentCommentId = Number(params);

    if (Number.isNaN(parentCommentId)) return undefined;

    return parentCommentId;
  })(searchParams?.get(SEARCH_PARAMS.PARENT_COMMENT_ID));

  if (!isEnumValue(COMMENT_SORT_TYPE, sortType))
    throw new CustomError({
      return_message: '잘못된 sortType입니다.',
    });

  const isLogin = useAuthStore((state) => state.isLogin);

  const router = useCustomRouter();

  const [placeholder, setPlaceholder] = useState(initialData);

  const isCommentPage = !!parentCommentId;

  const { toastPoint } = useToastPoint();

  return (
    <>
      <CommentList
        className={className}
        commentType={commentType}
        contentId={contentId}
        initialData={placeholder}
        rootCommentId={rootCommentId}
        sortType={sortType}
        onChangeCommentData={setPlaceholder}
        onClickEditButton={(commentId) => {
          router.push(
            ROUTES.COMMENT_EDIT.withSearchParams({
              dynamicPath: { commentId },
            }),
          );
        }}
        onClickReplyButton={(commentId, parentCommentId) => {
          const targetRoute = ROUTES.COMMENT_LIST.withSearchParams({
            dynamicPath: { commentType, contentId, commentId },
            searchParams: {
              sortType,
              parentCommentId,
            },
          });

          if (!isLogin) {
            return router.push(
              ROUTES.LOGIN.withSearchParams({
                searchParams: {
                  redirect: targetRoute,
                },
              }),
            );
          }

          return isCommentPage
            ? router.replace(targetRoute)
            : router.push(targetRoute);
        }}
      />
      {hasCommentField && isLogin ? (
        <CommentField
          className={commentFieldClassName}
          commentType={commentType}
          contentId={contentId}
          initOnFocus={initOnFocus}
          parentCommentId={parentCommentId}
          rootCommentId={rootCommentId}
          topDivider={commentFieldTopDivider}
          withPortal={withPortal}
          onSuccess={(res) => {
            if (!res) throw new CustomError();

            trackGtmEvent(GTM_EVENT_TYPE.POST_COMMENT, {
              content_name: policyName,
              comment_name: title,
              content_category: {
                [COMMENT_TYPE.POLICY]: CONTENT_CATEGORY.POLICY,
                [COMMENT_TYPE.POLICY_INFO]: CONTENT_CATEGORY.POLICY_NEWS,
                [COMMENT_TYPE.HOMETOWN_NEWS]: CONTENT_CATEGORY.HOMETOWN_NEWS,
                [COMMENT_TYPE.COMMUNITY]: CONTENT_CATEGORY.COMMUNITY,
              }[commentType],
              content_id: contentId.toString(),
            });

            if (sortType !== COMMENT_SORT_TYPE.LATEST) {
              const url = new URL(window.location.href);

              url.searchParams.set(
                SEARCH_PARAMS.SORT_TYPE,
                COMMENT_SORT_TYPE.LATEST,
              );

              history.replaceState(null, '', url.search);
            }

            scroller.scrollTo(SELECTOR.COMMENT_LIST_TOP, {
              duration: 300,
              smooth: 'easeOutQuad',
              containerId: SELECTOR.MAIN,
              offset: -100,
            });

            const point = res?.pointActivity?.resultYN
              ? res.pointActivity.addPoints
              : null;

            if (point) {
              toastPoint(point);
            }

            setTimeout(() => {
              scroller.scrollTo(SELECTOR.COMMENT_PREFIX + res.welloCommentId, {
                duration: 300,
                smooth: 'easeOutQuad',
                containerId: SELECTOR.MAIN,
                offset: -100,
              });
            }, 300);
          }}
        />
      ) : null}
    </>
  );
};
